import './string';
import join from 'url-join';
import { SubPath } from '../config.json';
import Moment from 'moment/moment';
import { smileDesignProductListArray } from '../store/SmileDesign/SmileDesignSlice';
import { RETAINER_REQUEST_REMOVE_STAGE, RETAINER_REQUEST_STATUS } from '../hooks/useRetainerRequest';
import { getAlignerLogMsg, handleRetainerRequesrtCaseLogDisplay } from './log_util';
import { CASE_STATUS, CASE_TYPE } from '../store/NewPatient/utils';

// this refinement_mode is controlling weather it is refinement or defect resubmit
let refinement_mode;

function isRenderDangerouslyInnerHtml(log) {
    const { status } = log;
    if (!status) return false;
    if (['RECEIVED', 'RETRACK_ALL_MATERIAL_RECEIVED'].includes(status?.status)) {
        if (status?.status_remarks && status?.status_remarks.length > 0) {
            return true;
        }
    }
    return false;
}

function interpolateStatus(msg, log) {
    const { status } = log;

    const matches = msg.matchAll(/{{([a-z0-9_]+)}}/gi);
    const adjectiveMap = { Moderately: 'Moderate', Severely: 'Severe' };
    for (const [token, varName] of matches) {
        let value = varName in status ? status[varName] || '' : '';
        if (varName === 'status_param2' && adjectiveMap[value]) {
            value = adjectiveMap[value];
        }
        msg = msg.replace(token, value);

        if (['RECEIVED', 'RETRACK_ALL_MATERIAL_RECEIVED'].includes(status?.status)) {
            const isVarValid = status[varName] && status[varName].length > 0;
            if (varName === 'status_remarks') {
                if (isVarValid) {
                    const splitedMsg = msg.split('-');
                    msg = `${splitedMsg[0]} <br><br>${this.get('@global.const.Reminder')}: ${status[varName]}`;
                } else {
                    // reset to default msg format for RECEIVED (support {2025-11-26} and {Nov 26 2025 (Wed)} format)
                    msg = this.get('@PatientInfo.log.RECEIVED')
                        .replace('{{status_param1}}', log?.status?.status_param1 || '')
                        .replace('{{status_param2}}', log?.status?.status_param2 || '');
                }
            }
        }
    }

    if (
        ['CHANGE_REQUEST', 'PLAN_APPROVED', 'ENDED', 'NEXT_SET_REQUEST', 'DEFECT_RESPONDED', 'RETRACK'].includes(
            status.status
        )
    ) {
        return { msg, external: true };
    }
    return msg;
}

function mergeRequests(logs) {
    // this function is filtering  3 duplicated case file id , case impression id and case detail id

    logs = logs.reverse().reduce(
        (acc, log) => {
            if (log) {
                if (log.case_retainer_request) {
                    acc.filtered.case_impression_id = [
                        ...(acc.filtered.case_impression_id || []),
                        log.case_impression.case_impression_id,
                    ];
                } else if (log.case_refinement) {
                    acc.filtered.case_detail_id = [
                        ...(acc.filtered.case_detail_id || []),
                        log.case_refinement.case_detail.case_detail_id,
                    ];
                    acc.filtered.case_file_id = [
                        ...(acc.filtered.case_file_id || []),
                        log.case_refinement.case_file.case_file_id,
                    ];
                    acc.filtered.case_impression_id = [
                        ...(acc.filtered.case_impression_id || []),
                        log.case_refinement.case_impression.case_impression_id,
                    ];
                } else if (
                    log.case_impression &&
                    acc.filtered.case_impression_id &&
                    acc.filtered.case_impression_id.includes(log.case_impression.case_impression_id)
                ) {
                    return acc;
                } else if (
                    log.case_file &&
                    acc.filtered.case_file_id &&
                    acc.filtered.case_file_id.includes(log.case_file.case_file_id)
                ) {
                    return acc;
                } else if (
                    log.case_detail &&
                    acc.filtered.case_detail_id &&
                    acc.filtered.case_detail_id.includes(log.case_detail.case_detail_id)
                ) {
                    return acc;
                }
                acc.results.push(log);
            }
            return acc;
        },
        { results: [], filtered: {} }
    ).results;
    return logs.reverse();
}

function udpateStatusParamInGetMessage(log, translationText) {
    const text = translationText;
    const regex = /{{status_param(\d+)}}/g;
    const updatedString = text.replace(regex, (_, param) => {
        const statusParam = parseInt(param);
        if (statusParam === 1) {
            return log.status.status_param1;
        } else if (statusParam === 2) {
            return log.status.status_param2;
        } else {
            return '';
        }
    });

    return updatedString;
}

function formatSelfDesignSteps(steps) {
    let parsedSteps = steps;
    if (typeof parsedSteps === 'string') {
        try {
            parsedSteps = JSON.parse(parsedSteps);
        } catch {
            return '';
        }
    }
    if (!Array.isArray(parsedSteps)) return '';
    const values = [...new Set(parsedSteps.map((step) => parseInt(step, 10)).filter(Number.isInteger))].sort(
        (a, b) => a - b
    );
    if (!values.length) return '';

    const ranges = [];
    let start = values[0];
    let end = values[0];
    values.slice(1).forEach((value) => {
        if (value === end + 1) {
            end = value;
            return;
        }
        ranges.push(start === end ? `${start}` : `${start}-${end}`);
        start = value;
        end = value;
    });
    ranges.push(start === end ? `${start}` : `${start}-${end}`);
    return ranges.join(', ');
}

function formatSelfDesignProduct(label, upperSteps, lowerSteps, get) {
    const arches = [
        [get('@DiffRetainer.formOptions.upper'), upperSteps],
        [get('@DiffRetainer.formOptions.lower'), lowerSteps],
    ]
        .map(([arch, steps]) => {
            const formattedSteps = formatSelfDesignSteps(steps);
            return formattedSteps ? `${arch}: ${formattedSteps}` : '';
        })
        .filter(Boolean);
    return arches.length ? `${label}: ${arches.join('; ')}` : '';
}

function formatSelfDesignDetails(selfDesign, get) {
    if (!selfDesign) return '';
    const additionalProducts = selfDesign.additional_products || {};
    const details = [
        formatSelfDesignProduct(
            get('log.SELF_DESIGN_ALIGNER'),
            selfDesign.upper_stages,
            selfDesign.lower_stages,
            get
        ),
        formatSelfDesignProduct(
            get('log.SELF_DESIGN_ATTACHMENT_TEMPLATES'),
            additionalProducts.MOS1100T?.upper_step,
            additionalProducts.MOS1100T?.lower_step,
            get
        ),
        formatSelfDesignProduct(
            get('log.SELF_DESIGN_PASSIVE_ALIGNER'),
            additionalProducts.MOS1100PA?.upper_step,
            additionalProducts.MOS1100PA?.lower_step,
            get
        ),
        formatSelfDesignProduct(
            get('log.SELF_DESIGN_RETAINER'),
            additionalProducts.MOS3021?.upper_step,
            additionalProducts.MOS3021?.lower_step,
            get
        ),
    ].filter(Boolean);

    const trimlineLabels = {
        gingivalCoverCutting: get('log.SELF_DESIGN_TRIMLINE_GINGIVAL_COVER'),
        scallopCutting: get('log.SELF_DESIGN_TRIMLINE_SCALLOP_CUTTING'),
    };
    const trimline = trimlineLabels[selfDesign.trimming_line_type];
    if (trimline) {
        details.push(`${get('log.SELF_DESIGN_TRIMLINE')}: ${trimline}`);
    }
    return details.join(' | ');
}

/** Translates case logs into display-ready messages based on the first-fit display flag. */
function translateLogs(
    logs,
    caseType,
    reverse = true,
    extraInfo = true,
    keepOri = false,
    mockupOrderOptionLabels = {},
    displayFirstFitFlag = false
) {
    let StatusMsgDict = {};

    if (caseType === 'RETAINER') {
        if (JSON.parse(localStorage.getItem('role')) === 'DR') {
            StatusMsgDict = {
                ADDITIONAL_INFORMATION: this.get('log.ADDITIONAL_INFORMATION'),
                DRAFT: this.get('log.DRAFT'),
                SUBMITTED: this.get('log.SUBMITTED'),
                AWAITING: `${this.get('log.AWAITING')} - {{status_remarks}}`,
                RECEIVED: this.get('log.RECEIVED'),
                PLAN_UPLOADED: this.get('log.TREATMENNT_PLAN_UPLOADED'),
                CHANGE_REQUEST: `Plan {{status_remarks}}: ${this.get('log.TREATMENNT_PLAN_CHANGED_REQUEST_RECEIVED')}`,
                PLAN_APPROVED: this.get('log.TREATMENNT_PLAN_APPROVED'),
                PRODUCTION: this.get('log.PRODUCTION'),
                DEFECT_UPLOADED: this.get('log.DEFECT_UPLOADED'),
                DEFECT_RESPONDED: this.get('log.DEFECT_RESPONDED'), //this.get('log.DEFECT_UPLOADED'),
                DELIVERED: this.get('log.RETAINER_SET_DELIVERED'),
                NEXT_SET_REQUEST: this.get('log.NEXT_SET_REQUEST'),
                ALL_DELIVERED: this.get('log.RETAINER_ALL_DELIVERED'),
                ENDED: this.get('log.ENDED'),
                REFINEMENT: this.get('log.REFINEMENTS'),
                DEFECT_RESUBMIT: this.get('log.DEFECT_RESUBMIT'),
            };
        } else {
            StatusMsgDict = {
                ADDITIONAL_INFORMATION: this.get('log.ADDITIONAL_INFORMATION'),
                DRAFT: this.get('log.DRAFT'),
                SUBMITTED: this.get('log.SUBMITTED'),
                AWAITING: `${this.get('log.AWAITING')} - {{status_remarks}}`,
                RECEIVED: this.get('log.RECEIVED'),
                PLAN_UPLOADED: this.get('log.TREATMENNT_PLAN_UPLOADED'),
                CHANGE_REQUEST: `Plan {{status_remarks}}: ${this.get('log.TREATMENNT_PLAN_CHANGED_REQUEST_RECEIVED')}`,
                PLAN_APPROVED: this.get('log.TREATMENNT_PLAN_APPROVED'),
                PRODUCTION: `{{status_remarks}} ${this.get('log.PRODUCTION')}`,
                DELIVERED: this.get('log.RETAINER_SET_DELIVERED'),
                NEXT_SET_REQUEST: this.get('log.NEXT_SET_REQUEST'),
                ALL_DELIVERED: this.get('log.RETAINER_ALL_DELIVERED'),
                ENDED: this.get('log.ENDED'),
                REFINEMENT: this.get('log.REFINEMENTS'),
                DEFECT_UPLOADED: this.get('log.DEFECT_UPLOADED'),
                DEFECT_RESPONDED: this.get('log.DEFECT_RESPONDED'), //this.get('log.DEFECT_UPLOADED'),
                DEFECT_RESUBMIT: this.get('log.DEFECT_RESUBMIT'),
            };
        }
    } else {
        StatusMsgDict = {
            ADDITIONAL_INFORMATION: this.get('log.ADDITIONAL_INFORMATION'),
            DRAFT: this.get('log.DRAFT'),
            SUBMITTED: this.get(caseType === CASE_TYPE.SELF_DESIGN ? 'log.SELF_DESIGN_SUBMITTED' : 'log.SUBMITTED'),
            AWAITING: `${this.get('log.AWAITING')} - {{status_remarks}}`,
            RECEIVED: `${this.get('log.RECEIVED')} - {{status_remarks}}`,
            PLAN_UPLOADED: this.get('log.TREATMENNT_PLAN_UPLOADED'),
            CHANGE_REQUEST: `Plan {{status_remarks}}: ${this.get('log.TREATMENNT_PLAN_CHANGED_REQUEST_RECEIVED')}`,
            PLAN_APPROVED: this.get('log.TREATMENNT_PLAN_APPROVED'),
            PRODUCTION: this.get('log.PRODUCTION'),
            DELIVERED: this.get('log.ALIGNER_SET_DELIVERED'),
            NEXT_SET_REQUEST: this.get('log.NEXT_SET_REQUEST'),
            ALL_DELIVERED: this.get('log.ALL_DELIVERED'),
            ENDED: this.get('log.ENDED'),
            REFINEMENT: this.get('log.REFINEMENTS'),
            DEFECT_UPLOADED: this.get('log.DEFECT_UPLOADED'),
            DEFECT_RESPONDED: this.get('log.DEFECT_RESPONDED'), //this.get('log.DEFECT_UPLOADED'),
            DEFECT_RESUBMIT: this.get('log.DEFECT_RESUBMIT'),
            RETRACK: this.get('log.RETRACK_REQUEST'),
            RETRACK_END: this.get('log.RETRACK_END'),
            RETRACK_REPORT: this.get('log.RETRACK_REPORT'),
            RETRACK_PRODUCTION_IN_PROGRESS: this.get('log.RETRACK_PRODUCTION_IN_PROGRESS'),
            RETRACK_CONTINUE: this.get('log.RETRACK_CONTINUE'),
            RETRACK_AWAITING: `${this.get('log.RETRACK_AWAITING')} - {{status_remarks}}`,
            RETRACK_ALL_MATERIAL_RECEIVED: `${this.get('log.RETRACK_ALL_MATERIAL_RECEIVED')} - {{status_remarks}}`,
            RESTORATION_TOOL_FORM_SUBMITTED: displayFirstFitFlag
                ? this.get('log.RESTORATION_TOOL_FORM_SUBMITTED_LOG_FIRSTFIT')
                : this.get('log.RESTORATION_TOOL_FORM_SUBMITTED_LOG'),
            MOCKUP_ORDER: this.get('log.MOCKUP_ORDER'),
            UPGRADE_REQUEST: this.get('log.UPGRADE_REQUEST'),
        };
    }

    if (extraInfo) {
        logs = mergeRequests(logs);
        // count treatment plan & retainer
        logs = logs.reduce(
            (acc, log) => {
                if (log.treatment_plan?.plan_id) {
                    if (log.treatment_plan.plan_no && log.treatment_plan.plan_id > 0) {
                        try {
                            if (log.status) {
                                log.status.plan_no = log.treatment_plan.plan_no;
                            }
                            acc.plan_ids[log.treatment_plan.plan_id] = log.treatment_plan.plan_no;
                            acc.plan_no = log.treatment_plan.plan_no;
                        } catch {}
                    } else {
                        try {
                            if (
                                log.treatment_plan.plan_id == -1 ||
                                !Object.keys(acc.plan_ids).includes(log.treatment_plan.plan_id.toString())
                            ) {
                                log.status.plan_no = log.treatment_plan.plan_no = ++acc.plan_no;
                                acc.plan_ids[log.treatment_plan.plan_id] = log.status.plan_no;
                            } else {
                                log.status.plan_no = log.treatment_plan.plan_no =
                                    acc.plan_ids[log.treatment_plan.plan_id];
                            }
                        } catch {}
                    }
                }
                if (log.case_retainer_request?.retainer_id) {
                    if (log.case_retainer_request.retainer_no) {
                        try {
                            if (log.status) {
                                log.status.retainer_no = log.case_retainer_request.retainer_no;
                            }
                            acc.retainer_ids[log.case_retainer_request.retainer_id] =
                                log.case_retainer_request.retainer_no;
                            acc.retainer_no = log.case_retainer_request.retainer_no;
                        } catch {}
                    } else {
                        try {
                            if (
                                log.case_retainer_request.retainer_id == -1 ||
                                !Object.keys(acc.retainer_ids).includes(log.case_retainer_request.retainer_id)
                            ) {
                                log.case_retainer_request.retainer_no = ++acc.retainer_no;
                                acc.retainer_ids[log.case_retainer_request.retainer_id] =
                                    log.case_retainer_request.retainer_no;
                            } else {
                                log.case_retainer_request.retainer_no =
                                    acc.retainer_ids[log.case_retainer_request.retainer_id];
                            }
                        } catch {}
                    }
                }
                if (log.case_refinement?.refinement_id) {
                    if (log.case_refinement.refinement_no) {
                        try {
                            if (log.status) {
                                // if it is refinement, log.status should be undefine.
                                log.status.refinement_no = log.case_refinement.refinement_no;
                            }
                            acc.refinement_ids[log.case_refinement.refinement_id] = log.case_refinement.refinement_no;
                            if (
                                log.case_refinement.refinement_no > 0 &&
                                log.case_refinement.refinement_no >= acc.refinement_no
                            ) {
                                // for checking the refinement cycle, add (R1) etc....

                                acc.refinement_no = log.case_refinement.refinement_no;
                            }
                            if (log.case_refinement.refinement_no !== acc.refinement_no) {
                                acc.plan_no = 0;
                                acc.retainer_no = 0;
                            }
                        } catch {}
                    } else {
                        try {
                            if (
                                log.case_refinement.refinement_id == -1 ||
                                !Object.keys(acc.refinement_ids).includes(log.case_refinement.refinement_id)
                            ) {
                                log.case_refinement.refinement_no = ++acc.refinement_no;
                                acc.plan_no = 0;
                                acc.retainer_no = 0;
                                acc.refinement_ids[log.case_refinement.refinement_id] =
                                    log.case_refinement.refinement_no;
                            } else {
                                log.case_refinement.refinement_no =
                                    acc.refinement_ids[log.case_refinement.refinement_id];
                            }
                        } catch {}
                    }
                }

                if (log.defect_resubmit?.refinement_id) {
                    if (log.defect_resubmit.refinement_no) {
                        try {
                            if (log.status) {
                                log.status.refinement_no = log.case_refinement.refinement_no;
                            }

                            acc.defect_resubmit_ids[log.defect_resubmit.refinement_id] =
                                log.defect_resubmit.refinement_no;

                            if (
                                log.defect_resubmit.refinement_no > 0 &&
                                log.defect_resubmit.refinement_no >= acc.refinement_no
                            ) {
                                acc.refinement_no = log.defect_resubmit.refinement_no;
                            }

                            if (log.defect_resubmit.refinement_no !== acc.refinement_no) {
                                acc.plan_no = 0;
                                acc.retainer_no = 0;
                            }
                        } catch (error) {
                            console.error(error);
                        }
                    } else {
                        try {
                            if (
                                log.defect_resubmit.refinement_no == -1 ||
                                !Object.keys(acc.defect_resubmit_ids).includes(log.defect_resubmit.refinement_id)
                            ) {
                                log.defect_resubmit.refinement_no = ++acc.defect_resubmit_no;
                                acc.plan_no = 0;
                                acc.retainer_no = 0;
                                acc.defect_resubmit_ids[log.defect_resubmit.refinement_id] =
                                    log.defect_resubmit.refinement_no;
                            } else {
                                log.defect_resubmit.refinement_no =
                                    acc.defect_resubmit_ids[log.defect_resubmit.refinement_id];
                            }
                        } catch (error) {
                            console.error(error);
                        }
                    }
                }

                // log.refinement_no = acc.refinement_no

                acc.logs.push(log);
                return acc;
            },
            {
                logs: [],
                plan_ids: {},
                plan_no: 0,
                retainer_ids: {},
                retainer_no: 0,
                refinement_ids: {},
                refinement_no: 0,
                defect_resubmit_ids: {},
                defect_resubmit_no: 0,
            }
        ).logs;
    }

    logs = logs.filter((log) => {
        const retainerRequestLogDisplayBool = handleRetainerRequesrtCaseLogDisplay(log);
        return (
            // condition checking for "UPLOAD" type
            (log.type !== 'UPLOAD' ||
                log.case_refinement?.refinement_id ||
                log.defect_resubmit?.refinement_id ||
                !(
                    log.case_detail?.refinement_no ||
                    log.case_file?.refinement_no ||
                    (!(log.case_retainer_request?.retainer_id > 0) && log.case_impression?.refinement_no)
                ) ||
                /* shortened to log.refinement > 0 */
                // // it is for not filtering admin upload case log in defect resubmit
                // || (log.refinement_no>0 && log.case_file?.defect_resubmit_flag===true)
                // || (log.refinement_no>0 && log.case_impression?.defect_resubmit_flag===true)
                // || (log.refinement_no>0 && log.case_detail?.defect_resubmit_flag===true)
                // // it is for not filtering admin upload case log in refinement
                // || (log.refinement_no>0 && log.case_file?.defect_resubmit_flag===false)
                // || (log.refinement_no>0 && log.case_impression?.defect_resubmit_flag===false)
                // || (log.refinement_no>0 && log.case_detail?.defect_resubmit_flag===false)
                log.refinement_no > 0 ||
                // cs / DISTR help designer design file
                ['SADMIN', 'ADMIN', 'DISTR'].includes(log.role)) &&
            // condition checking for "UPDATE" type
            (log.type !== 'UPDATE' ||
                !(log.case_detail || log.case_file || log.case_impression || log.case_retainer_request) ||
                // Purpose: Retainer must add flag here for showing delivered case activity log
                log.case_finishing_retainer?.delivered_flag ||
                log.case_finishing_retainer?.production_flag ||
                // Below for retainer request
                retainerRequestLogDisplayBool.showDelivered ||
                retainerRequestLogDisplayBool.showProductionInProgress) &&
            retainerRequestLogDisplayBool.showRetainerRequest
        );
    });

    logs = logs.map((log, i, logs) => ({
        refinementNo: log.refinement_no ?? 0,
        defect_resubmitNo: log.refinement_no ?? 0,
        date: {
            friendly: Moment(log.created_date).fromNow(),
            formal: Moment(log.created_date).format('DD-MMM-YYYY  HH:mm:ss'),
        },
        logId: log.case_activity_log_id,
        msg: getMessage.bind(this)(log, StatusMsgDict, caseType, mockupOrderOptionLabels),
        ...(extraInfo
            ? {
                  btn: getButton.bind(this)(log, log.case_id, i, logs.length, caseType, displayFirstFitFlag),
              }
            : null),
        ...(keepOri
            ? {
                  log,
              }
            : null),
        isRenderDangerouslySetInnerHtml: isRenderDangerouslyInnerHtml(log),
    }));

    if (reverse) logs.reverse();
    return logs;
}

function createButton(retainerId, url) {
    return {
        onClick: () => this.handleRollbackUpdate(url, 'Please Input Remove Reason.'),
        value: this.get('log.DELETE_RETAINER'),
        id: retainerId,
    };
}

function getButton(log, caseId, log_count, total, caseType, displayFirstFitFlag) {
    const btn = [];
    if (typeof log.case_file === 'object' && ['UPLOAD', 'UPDATE'].includes(log.type)) {
        if (log.case_file.radio0 || log.case_file.radio1 || log.case_file.radio2) {
            btn.push({
                onClick: (_) => this.handleOpen('radiograph', 'view', { id: log.case_file.case_file_id }),
                value: this.get('log.VIEW_RADIOGRAPHS'),
                id: log.case_file.case_file_id,
            });
        } else if (!log.case_finishing_retainer?.finishing_retainer_id) {
            btn.push({
                onClick: (_) => this.handleOpen('photo', 'view', { id: log.case_file.case_file_id }),
                value: this.get('log.VIEW_PHOTOS'),
                id: log.case_file.case_file_id,
            });
        }
    }

    if (typeof log.case_defect === 'object') {
        if (log.case_defect.defect_finished_flag != 2) {
            //not rescan action

            btn.push({
                onClick: (_) => this.handleOpen('defect'),
                value:
                    log.case_defect.defect_finished_flag == 1
                        ? this.get('log.viewDefectPhotos')
                        : this.get('log.viewDefectPhotos'),
                id: log.case_defect.defect_id,
            });
        }

        if (log.case_defect.defect_finished_flag == 2) {
            //rescan action
            if (log.status.status == 'DEFECT_RESPONDED') {
                if (log.case_defect.defect_finished_flag == 1) {
                    btn.push({
                        onClick: (_) => this.handleOpen('defect'),
                        value:
                            log.case_defect.defect_finished_flag == 1
                                ? this.get('log.viewDefectPhotos')
                                : this.get('log.viewDefectPhotos'),
                        id: log.case_defect.defect_id,
                    });
                } else {
                    btn.push({
                        newtab: true,
                        url: join(
                            SubPath,
                            `clinicalSimulation/${this.props.match.params.id}/plan/${log.status?.status_param2}`
                        ),
                        value: this.get('log.CLINICAL_SIMULATION'),
                    });
                }
            } else {
                btn.push({
                    onClick: (_) => this.handleOpen('defect'),
                    value:
                        log.case_defect.defect_finished_flag == 1
                            ? this.get('log.viewDefectPhotos')
                            : this.get('log.viewDefectPhotos'),
                    id: log.case_defect.defect_id,
                });
            }
        }
    }
    if (typeof log.case_impression === 'object' && ['UPLOAD', 'UPDATE'].includes(log.type)) {
        if (log.status?.status_param1 !== 'Bleaching Tray') {
            btn.push({
                onClick: (_) => this.handleOpen('impression', 'view', { id: log.case_impression.case_impression_id }),
                value: this.get('log.VIEW_IMPRESSION'),
                id: log.case_impression.case_impression_id,
            });
        }
    }

    if (typeof log.case_refinement === 'object') {
        if (log.case_refinement.refinement_id > 0) {
            btn.push({
                onClick: (_) =>
                    this.handleOpen('refinement', 'view', {
                        refinement_no: log.case_refinement.refinement_no,
                        comment: log.case_refinement.comment,
                    }),
                value: this.get('log.VIEW_REFINEMENT'),
                id: log.case_refinement.refinement_id,
            });
        }
    }

    // Purpose: View Restoration tool form
    if (log.status?.status === 'RESTORATION_TOOL_FORM_SUBMITTED') {
        if(displayFirstFitFlag) {
            btn.push({
                onClick: (_) =>
                    this.handleOpen('viewRestorationFormFirstFitModal', 'view', {
                        case_activity_log_id: log.case_activity_log_id,
                        case_id: log.case_id,
                        restorationFormId: log.restoration_form_id,
                        viewType: 'mockupFeedback',
                    }),
                value: this.get('log.VIEW_MOCKUP_FEEDBACK'),
                id: log.case_activity_log_id,
                requiresMockupOrder: true,
            });
            btn.push({
                onClick: (_) =>
                    this.handleOpen('viewRestorationFormFirstFitModal', 'view', {
                        case_activity_log_id: log.case_activity_log_id,
                        case_id: log.case_id,
                        restorationFormId: log.restoration_form_id,
                        viewType: 'prescription',
                    }),
                value: this.get('log.VIEW_RESTORATION_TOOL_FORM_FIRSTFIT'),
                id: log.case_activity_log_id,
            });
            btn.push({
                onClick: (_) =>
                    this.handleOpen('viewRestorationFormFirstFitModal', 'view', {
                        case_activity_log_id: log.case_activity_log_id,
                        case_id: log.case_id,
                        restorationFormId: log.restoration_form_id,
                        viewType: 'impression',
                        caseImpressionId: log.case_impression?.case_impression_id || log.case_impression_id || null,
                    }),
                value: this.get('log.VIEW_IMPRESSIONS'),
                id: log.case_activity_log_id,
            });
        }else{
            btn.push({
                onClick: (_) =>
                    this.handleOpen('viewRestorationToolFormModal', 'view', {
                        case_activity_log_id: log.case_activity_log_id,
                        case_id: log.case_id,
                        restorationFormId: log.restoration_form_id,
                    }),
                value: this.get('log.VIEW_RESTORATION_TOOL_FORM'),
                id: log.case_activity_log_id,
            });
        }
    }

    if (log.status?.status_param2 === 'MOCKUP_ORDER') {
        btn.push({
            onClick: () =>
                this.handleOpen('viewMockupOrderModal', 'view', {
                    mockupOrderId: log.mockup_order_id,
                    mockupRequestId: log.status.status_param1
                }),
            value: this.get('log.VIEW_MOCKUP_ORDER'),
            id: log.case_activity_log_id,
        });
    }

    // Purpose: View Retrack
    if (typeof log.case_retrack === 'object') {
        if (
            log.case_retrack.retrack_id > 0 &&
            log.case_retrack.case_file.case_file_id !== null &&
            log.case_retrack.case_impression.case_impression_id !== null
        ) {
            btn.push({
                onClick: (_) =>
                    this.handleOpen('viewRetrackDataModal', 'view', {
                        retrack_no: log.case_retrack.retrack_no,
                        retrack_id: log.case_retrack.retrack_id,
                        case_activity_log_id: log.case_activity_log_id,
                    }),
                value: this.get('log.VIEW_RETRACK'),
                id: log.case_retrack.retrack_id,
            });
        }
    }

    // Purpose: View Retrack Report
    if (
        typeof log.case_retrack === 'object' &&
        (log.status.status === 'RETRACK_REPORT' ||
            (log.status.status === 'RETRACK_END' && log.status.status_param1 !== 'CANCEL'))
    ) {
        if (log.case_retrack.retrack_id > 0 && log.case_retrack.case_retrack_detail.case_retrack_report_id !== null) {
            btn.push({
                onClick: (_) =>
                    this.handleOpen('viewRetrackReportModal', 'view', {
                        retrack_no: log.case_retrack.retrack_no,
                        retrack_id: log.case_retrack.retrack_id,
                        case_retrack_report_id: log.case_retrack.case_retrack_detail.case_retrack_report_id,
                        case_activity_log_id: log.case_activity_log_id,
                    }),
                // value: 'View ReTrack Report',
                value: this.get('log.VIEW_RETRACK_REPORT'),
                id: log.case_retrack.retrack_id,
            });
        }
    }

    if (typeof log.defect_resubmit === 'object') {
        if (log.defect_resubmit.refinement_id > 0) {
            btn.push({
                onClick: (_) =>
                    this.handleOpen('refinement', 'view', {
                        refinement_no: log.defect_resubmit.refinement_no,
                        comment: log.defect_resubmit.comment,
                        defect_resubmit: true,
                    }),
                value: this.get('log.VIEW_DEFECT_RESUBMIT'),
                id: log.defect_resubmit.refinement_id,
            });
        }
    }
    if (typeof log.case_retainer_request === 'object') {
        if (log.case_retainer_request.retainer_id && !log.case_retainer_request.retainer_request_id) {
            const caseImpressionId = log.case_impression.case_impression_id || null;
            // for retainer request before order different retainer feature
            if (!log.case_retainer_request.delivered_flag && ['ADMIN', 'SADMIN', 'DISTR'].includes(this.state.role)) {
                btn.push({
                    onClick: (_) =>
                        this.handleInstantUpdate(
                            `/case/${caseId}/retainer_request/${log.case_retainer_request.retainer_id}/delivered/${caseImpressionId}`,
                            'Mark this Retainer Delivered?'
                        ),
                    value: this.get('log.MARK_DELIVERED'),
                    id: log.case_retainer_request.retainer_id,
                });
            }

            if (!log.case_retainer_request.delivered_flag && ['ADMIN', 'SADMIN', 'DISTR'].includes(this.state.role)) {
                btn.push(
                    createButton.call(
                        this,
                        log.case_retainer_request.retainer_id,
                        `/case/${caseId}/retainer_request/${log.case_retainer_request.retainer_id}/delete`
                    )
                );
            }
        } else if (log.case_retainer_request.retainer_id && log.case_retainer_request.retainer_request_id) {
            // for retainer request after order different retainer feature
            const isRetainerRequestValid =
                log.case_retainer_request && log.case_retainer_request && log.case_retainer_request.retainer_request_id
                    ? true
                    : false;
            if (isRetainerRequestValid) {
                // all roles can view the retainer request prescription
                btn.push({
                    onClick: () =>
                        this.handleOpen('retainer_request_prescription', 'view', {
                            id: log.case_retainer_request.retainer_request_id,
                        }),
                    value: this.get('log.VIEW_QUESTIONNAIRE'),
                    id: log.case_retainer_request.retainer_request_id,
                });

                // below btns are for admin roles
                const isAdmin = ['ADMIN', 'SADMIN', 'DISTR'].includes(this.state.role);
                if (isAdmin) {
                    // operation flow: mark production -> mark delivered

                    // if status === null means it is in request stage
                    const isRequest =
                        log.case_retainer_request.status === null ||
                        log.case_retainer_request.status === RETAINER_REQUEST_STATUS.RETAINER_REQUEST_REQ;
                    const isProductionInProgress = log.case_retainer_request.production_flag;
                    const isDeliveredFlag = log.case_retainer_request.delivered_flag;

                    // BTN status flow : null => RETAINER_REQUEST_REQ => RETAINER_REQUEST_PRO => RETAINER_REQUEST_DEL

                    if (log.case_retainer_request.status === null) {
                        const url = `/case/${caseId}/retainer_request/${log.case_retainer_request.retainer_request_id}/delete/${RETAINER_REQUEST_REMOVE_STAGE.REQUEST}/${log.case_activity_log_id}`;
                        btn.push(createButton.call(this, log.case_retainer_request.retainer_request_id, url));
                    }

                    // step 1: mark production
                    if (!isProductionInProgress) {
                        btn.push({
                            onClick: () =>
                                this.handleInstantUpdate(
                                    `/case/retainerRequest_production/${log.case_retainer_request.retainer_request_id}/${log.case_retainer_request.retainer_id}`,
                                    'Mark this Retainer production'
                                ),
                            value: this.get('log.PRODUCTION_IN_PROGRESS'),
                            id: log.case_retainer_request.retainer_request_id,
                        });
                    }

                    // step 2: mark delivered (first it need to be in production in progress and not delivered yet)
                    if (isProductionInProgress && !isDeliveredFlag) {
                        if (!isRequest) {
                            btn.push({
                                onClick: (_) =>
                                    this.handleInstantUpdate(
                                        `/case/retainerRequest_delivered/${log.case_retainer_request.retainer_request_id}/${log.case_retainer_request.retainer_id}}`,
                                        'Mark this Retainer Delivered?'
                                    ),
                                value: this.get('log.MARK_DELIVERED'),
                                id: log.case_retainer_request.retainer_id,
                            });

                            const url = `/case/${caseId}/retainer_request/${log.case_retainer_request.retainer_request_id}/delete/${RETAINER_REQUEST_REMOVE_STAGE.PRODUCTION}/${log.case_activity_log_id}`;
                            btn.push(createButton.call(this, log.case_retainer_request.retainer_request_id, url));
                        }
                    }

                    // step 3: remove request (first it need to be in delivered status)
                    if (isDeliveredFlag) {
                        // ensure the retainer request is in delivered status
                        if (
                            log.case_retainer_request.status &&
                            log.case_retainer_request.status === RETAINER_REQUEST_STATUS.RETAINER_REQUEST_DEL
                        ) {
                            const url = `/case/${caseId}/retainer_request/${log.case_retainer_request.retainer_request_id}/delete/${RETAINER_REQUEST_REMOVE_STAGE.DELIVERED}/${log.case_activity_log_id}`;
                            btn.push(createButton.call(this, log.case_retainer_request.retainer_request_id, url));
                        }
                    }
                }
            }
        }
    }

    if (typeof log.aligner_request === 'object') {
        if (!log.aligner_request.delivered_flag && ['ADMIN', 'SADMIN', 'DISTR'].includes(this.state.role)) {
            btn.push({
                onClick: (_) =>
                    this.handleInstantUpdate(
                        `/case/${caseId}/aligner_request/${log.aligner_request.request_id}/delivered`,
                        'Mark this Aligner Set Delivered?'
                    ),
                value: this.get('log.MARK_DELIVERED'),
                id: log.aligner_request.request_id,
            });
        }
    }

    if (
        log.status?.status === 'NEXT_SET_REQUEST' &&
        parseInt(log_count) + 1 === total &&
        ['ADMIN', 'SADMIN', 'DISTR'].includes(this.state.role)
    ) {
        btn.push(createButton.call(this, log.case_activity_log_id, `/case/${caseId}/NEXT_SET_REQUEST/rollback`));
    }
    if (log.status?.status === 'CHANGE_REQUEST') {
        if (log.status?.status_param2) {
            btn.push({
                newtab: true,
                url: join(
                    SubPath,
                    `clinicalSimulation/${this.props.match.params.id}/plan/${log.status?.status_param2}`
                ),
                value: this.get('log.CLINICAL_SIMULATION'),
            });
        }
    }

    const isSelfDesignDeliveredLog = caseType === CASE_TYPE.SELF_DESIGN && log.status?.status === CASE_STATUS.DELIVERED;
    if (
        !isSelfDesignDeliveredLog &&
        typeof log.treatment_plan === 'object' &&
        (log.type !== 'UPDATE' || caseType === CASE_TYPE.SELF_DESIGN)
    ) {
        if (caseType === CASE_TYPE.SELF_DESIGN) {
            btn.push({
                newtab: true,
                url: join(SubPath, `meditReview/${caseId}?planNo=${log.treatment_plan.plan_no}`),
                value: this.get('log.DESIGN_SIMULATION'),
            });
        } else {
            btn.push({
                newtab: true,
                url: join(
                    SubPath,
                    `clinicalSimulation/${this.props.match.params.id}/plan/${log.treatment_plan.plan_id}`
                ),
                value: log.isSmileDesign ? this.get('log.SMILE_DESIGN_VIEWER') : this.get('log.CLINICAL_SIMULATION'),
            });
        }
    }
    if (
        log.status?.status === 'PLAN_UPLOADED' ||
        (caseType === CASE_TYPE.SELF_DESIGN && log.type === 'UPDATE' && log.treatment_plan?.plan_id)
    ) {
        btn.push({
            newtab: true,
            url: join(SubPath, `patients/${this.props.match.params.id}/treatment`),
            value: log.isSmileDesign ? this.get('log.viewFullDesignDocument') : this.get('log.VIEW_TREATMENT_PLAN'),
        });
    }
    if (typeof log.stl_pair === 'object') {
        btn.push({
            onClick: (_) => this.handleOpen('stl', 'view', { id: log.stl_pair.pair_id }),
            value: this.get('log.VIEW_STL_FILES'),
            id: log.stl_pair.pair_id,
        });
    }

    // Finishing Retainer
    if (
        log.case_finishing_retainer?.finishing_retainer_id &&
        ['ADMIN', 'SADMIN', 'DISTR', 'DR'].includes(this.state.role)
    ) {
        //Purpose: View Finishing Retainer
        if (typeof log.case_finishing_retainer === 'object') {
            if (log.case_finishing_retainer.finishing_retainer_id) {
                btn.push({
                    onClick: (_) =>
                        this.handleOpen('viewFinishingRetainerModal', 'view', {
                            case_activity_log_id: log.case_activity_log_id,
                            finishing_retainer_id: log.case_finishing_retainer.finishing_retainer_id,
                        }),
                    value: this.get('log.VIEW_FINISHING_RETAINER'),
                    id: log.case_finishing_retainer.finishing_retainer_id,
                });
            }
        }

        if (['ADMIN', 'SADMIN', 'DISTR'].includes(this.state.role)) {
            //Button: Mark Production
            if (!log.case_finishing_retainer.delivered_flag && !log.case_finishing_retainer.production_flag) {
                btn.push({
                    onClick: () =>
                        this.handleInstantUpdate(
                            `case/finishing_retainer_production/${log.case_finishing_retainer?.finishing_retainer_id}`,
                            'Mark this Retainer production'
                        ),
                    value: this.get('log.PRODUCTION_IN_PROGRESS'),
                    id: log.case_finishing_retainer?.finishing_retainer_id,
                });

                //Button: Remove request in request
                btn.push(
                    createButton.call(
                        this,
                        log.case_finishing_retainer.finishing_retainer_id,
                        `/case/${caseId}/finishing_retainer/${log.case_finishing_retainer.finishing_retainer_id}/delete/request/${log.case_activity_log_id}`
                    )
                );
            }

            //Button: Mark Delivered
            if (log.case_finishing_retainer.status_param2 === 'RETAINER_PRODUCTION') {
                if (!log.case_finishing_retainer.delivered_flag) {
                    btn.push({
                        onClick: () =>
                            this.handleInstantUpdate(
                                `case/finishing_retainer_delievered/${log.case_finishing_retainer?.finishing_retainer_id}`,
                                'Mark this Retainer Delivered?'
                            ),
                        value: this.get('log.MARK_DELIVERED'),
                        id: log.case_finishing_retainer?.finishing_retainer_id,
                    });
                    //Button: Remove request in production
                    btn.push(
                        createButton.call(
                            this,
                            log.case_finishing_retainer.finishing_retainer_id,
                            `/case/${caseId}/finishing_retainer/${log.case_finishing_retainer.finishing_retainer_id}/delete/production/${log.case_activity_log_id}`
                        )
                    );
                }
            }

            //Button: Remove request in delivered
            if (
                log.case_finishing_retainer.delivered_flag &&
                log.case_finishing_retainer.production_flag &&
                log.case_finishing_retainer.status_param2 !== 'RETAINER_PRODUCTION' &&
                log.type !== 'UPLOAD'
            ) {
                btn.push(
                    createButton.call(
                        this,
                        log.case_finishing_retainer.finishing_retainer_id,
                        `/case/${caseId}/finishing_retainer/${log.case_finishing_retainer.finishing_retainer_id}/delete/delivered/${log.case_activity_log_id}`
                    )
                );
            }
        }
    }

    if (log.type === 'STATUS' && log.has_case_supporting_file) {
        let caseStatus = log?.status?.status;
        let btnText = this.get('log.REMINDER_DETAILS');
        if (log && log?.status) {
            const { status } = log?.status;
            if (status) {
                caseStatus = status;
                if (['AWAITING', 'RETRACK_AWAITING'].includes(status)) {
                    btnText = this.get('log.VIEW_DETAILS');
                }
            }
        }
        btn.push({
            onClick: () =>
                this.handleOpen('view_case_supporting_file_modal', 'view', {
                    id: log.case_activity_log_id,
                    caseLogCreatedDate: log.created_date,
                    caseLogStatus: caseStatus,
                }),
            value: btnText,
        });
    }

    if (
        caseType === CASE_TYPE.SELF_DESIGN &&
        ['PLAN_APPROVED', CASE_STATUS.DELIVERED].includes(log.status?.status) &&
        log.treatment_plan?.plan_id
    ) {
        btn.push({
            onClick: () =>
                this.handleOpen('viewManufactureDetailModal', 'view', {
                    planId: log.treatment_plan.plan_id,
                    planNo: log.treatment_plan?.plan_no,
                    caseId: log.case_id,
                }),
            value: this.get('log.MANUFACTURE_DETAIL'),
            id: log.case_activity_log_id,
        });
    }

    return btn;
}

/** Builds the display message for a single case log. */
function getMessage(log, StatusMsgDict = {}, caseType, mockupOrderOptionLabels = {}) {
    const bindGetAlignerLogMsg = getAlignerLogMsg.bind(this);

    let retMsg = this.get('log.UNKNOWN') + `case log id => ${log.case_activity_log_id}`;
    switch (log.type) {
        case 'STATUS':
            const bindGetTranslationInterpolateStatus = interpolateStatus.bind(this);
            retMsg = bindGetTranslationInterpolateStatus(StatusMsgDict[log.status.status], log);

            // for new simplified delivered log message of retainer case
            if (log.status.status === CASE_STATUS.DELIVERED) {
                if (caseType === CASE_TYPE.RETAINER) {
                    let statusObj = log.status;
                    const { status_param1, status_param2 } = statusObj;
                    if (!status_param1 && !status_param2) {
                        retMsg = { ...retMsg, msg: this.get('log.RETAINER_SET_DELIVERED_V2') };
                    }
                }
            }

            if (log.status.status == 'DEFECT_RESPONDED' && log.status.status_remarks == 'Retake') {
                let retakeLog = this.get('log.DEFECT_RESPONDED_RETAKE');
                retakeLog = retakeLog.charAt(0).toUpperCase() + retakeLog.slice(1);
                retMsg = { ...retMsg, msg: retakeLog };
            }
            if (log.status.status == 'DEFECT_RESPONDED' && log.status.status_remarks != 'Retake') {
                let defectType = log.case_defect.defect_distorted;
                defectType = defectType.charAt(0).toUpperCase() + defectType.slice(1);
                retMsg = { ...retMsg, msg: defectType + ' ' + retMsg.msg };
            }
            if (['DELIVERED', 'ALL_DELIVERED'].includes(log.status.status) && log?.case_retrack?.retrack_id) {
                let track_status = log.case_retrack.case_retrack_detail.track_status;
                switch (track_status) {
                    case 'ONTRACK': {
                        retMsg = {
                            ...retMsg,
                            msg: udpateStatusParamInGetMessage(log, this.get('log.RETRACK_DELIVERED')),
                        };
                        break;
                    }
                    case 'OFFTRACK': {
                        if (log.status.status === 'DELIVERED') {
                            retMsg = {
                                ...retMsg,
                                msg:
                                    this.get('@PatientInfo.caseStatusLabel.RETRACK') +
                                    ' ' +
                                    udpateStatusParamInGetMessage(log, this.get('log.ALIGNER_SET_DELIVERED')),
                            };
                        } else if (log.status.status === 'ALL_DELIVERED') {
                            retMsg = {
                                ...retMsg,
                                msg: udpateStatusParamInGetMessage(log, this.get('log.ALL_DELIVERED')),
                            };
                        }
                        break;
                    }
                    // Explain: in this stage the case log must not have msg when NONE if ['DELIVERED', 'ALL_DELIVERED'].includes(log.status.status) && log?.case_retrack?.retrack_id
                    case 'NONE': {
                        retMsg = { ...retMsg, msg: 'track_status error' };
                        break;
                    }
                }
            }

            if (log.status.status == 'RETRACK_END') {
                if (log.status.status_param1 === 'CANCEL') {
                    retMsg = this.get('log.RETRACK_CANCEL');
                } else {
                    const text = this.get('log.RETRACK_END_REFINEMENT');
                    let updatedString;
                    if (log.status.status_remarks === null) {
                        //Purpose: for development testing. Some test case created with no reason provided before will crash in patientInfo page
                        updatedString = text.replace('{{status_remarks}}', log.status.status_remarks);
                    } else {
                        // Remove: try since old requirement
                        try {
                            const remarks = JSON.parse(log.status.status_remarks);
                            let transformText;
                            switch (remarks[0]) {
                                case 'length': {
                                    transformText =
                                        this.get(`log.RETRACK_END_REASON.choice.${remarks[1]}`) +
                                        ' ' +
                                        this.get('log.RETRACK_END_REASON.of') +
                                        ' ' +
                                        remarks[2] +
                                        ` ${this.get('log.RETRACK_END_REASON.is')} ` +
                                        remarks[3] +
                                        ' ' +
                                        this.get('log.RETRACK_END_REASON.choice.length');
                                    break;
                                }
                                case 'angle': {
                                    transformText =
                                        this.get(`log.RETRACK_END_REASON.choice.${remarks[1]}`) +
                                        ' ' +
                                        this.get('log.RETRACK_END_REASON.of') +
                                        ' ' +
                                        remarks[2] +
                                        ` ${this.get('log.RETRACK_END_REASON.is')} ` +
                                        remarks[3] +
                                        ' ' +
                                        this.get('log.RETRACK_END_REASON.choice.angle');
                                    break;
                                }
                                case 'attachment': {
                                    transformText =
                                        this.get(`log.RETRACK_END_REASON.choice.${remarks[1]}`) +
                                        ' ' +
                                        this.get('log.RETRACK_END_REASON.choice.attachment');
                                    break;
                                }
                            }
                            updatedString = text.replace('{{status_remarks}}', transformText);
                            retMsg = updatedString;
                        } catch (e) {
                            // remain for new requirement
                            retMsg = this.get(`@PatientInfo.log.RETRACK_END_REASON.${log.status.status_remarks}`);
                        }
                    }
                }
            }
            if (log.status.status === 'RETRACK_REPORT') {
                const text = this.get('log.RETRACK_REPORT');
                const regex = /{{status_param(\d+)}}/g;
                const updatedString = text.replace(regex, (_, param) => {
                    const statusParam = parseInt(param);
                    if (statusParam === 1) {
                        return log.status.status_param1;
                    } else if (statusParam === 2) {
                        return log.status.status_param2;
                    } else {
                        return '';
                    }
                });
                retMsg =
                    log.status.status_param1 === null && log.status.status_param2 === null
                        ? // 'Retrack Report uploaded, TrioClear aligner sets work well. Retrack is finished':
                          this.get('log.RETRACK_REPORT_NULL')
                        : updatedString;
            }
            let approvementTypeLabel = null
            if(log.status?.status === 'PLAN_APPROVED'){
                switch (log.smile_design_approvement_option?.option_approvement) {
                    case "option.approveiDesign2AndSmileDesign":
                        approvementTypeLabel = 'iDesign & I.S.D.';
                        break;
                    case "option.approveSmileDesign":
                        approvementTypeLabel = 'I.S.D.';
                        break;
                    case null:
                        approvementTypeLabel = 'iDesign';
                        break;
                    default:
                        approvementTypeLabel = null;
                        break;
                }
            }
            if (log.status?.status === 'PLAN_APPROVED' && log?.case_type_update?.case_type_to) {
                const text = this.get('log.TREATMENNT_PLAN_APPROVED');
                const regex = /{{plan_no}}/g;
                const updatedString = text.replace(regex, (_, param) => {
                    return log.treatment_plan.plan_no;
                });
                let myMsg;
                if (
                    [log.case_type_update.case_type_from_step_no, log.case_type_update.case_type_from].includes(
                        log.case_type_update.case_type_to
                    )
                ) {
                    myMsg = updatedString + (approvementTypeLabel ? `(${approvementTypeLabel})` : '') + ' ' + ' - ' + this.get(`:caseType.${log.case_type_update.case_type_to}`);
                } else {
                    myMsg =
                        updatedString  + (approvementTypeLabel ? `(${approvementTypeLabel})` : '') +
                        ' ' +
                        ' - ' +
                        this.get(
                            `:caseType.${log.case_type_update.case_type_from_step_no ? log.case_type_update.case_type_from_step_no : log.case_type_update.case_type_from}`
                        ) +
                        ' to ' +
                        this.get(`:caseType.${log.case_type_update.case_type_to}`) +
                        ' (Upgraded)';
                }

                retMsg = { msg: myMsg, external: true };
            }

            if (log.status?.status === 'PLAN_UPLOADED' && log?.case_type_process?.case_type_to) {
                const text = this.get('log.TREATMENNT_PLAN_UPLOADED');
                const regex = /{{plan_no}}/g;
                const updatedString = text.replace(regex, (_, param) => {
                    return log.treatment_plan.plan_no;
                });

                let myMsg;
                if (
                    log.case_type_process.case_type_to === log.case_type_process.case_type_from ||
                    log.case_type_process.case_type_from === 'CHOOSE_LATER'
                ) {
                    myMsg = updatedString;
                } else {
                    myMsg =
                        updatedString +
                        ' ' +
                        ' - ' +
                        this.get(`:caseType.${log.case_type_process.case_type_from}`) +
                        ' to ' +
                        this.get(`:caseType.${log.case_type_process.case_type_to}`) +
                        ' (Upgraded)';
                }

                retMsg = { msg: myMsg, external: false };
            }

            // Purpose: if doctor choose Fill, then admin will choose aligner set x - x. In this case, status_param1 and status_param2 will be none
            if (
                log.status.status === 'RETRACK_PRODUCTION_IN_PROGRESS' &&
                log.status.status_param1 !== null &&
                log.status.status_param2 !== null
            ) {
                const text = this.get('log.RETRACK_PRODUCTION_IN_PROGRESS_DELIVERED');
                const regex = /{{status_param(\d+)}}/g;
                const updatedString = text.replace(regex, (_, param) => {
                    const statusParam = parseInt(param);
                    if (statusParam === 1) {
                        return log.status.status_param1;
                    } else if (statusParam === 2) {
                        return log.status.status_param2;
                    } else {
                        return '';
                    }
                });
                retMsg = updatedString;
            }

            if (log.status.status === 'UPGRADE_REQUEST') {
                if (log.smile_design_upgrade_confirm.plan === 'comprehensive') {
                    // retMsg = this.get('log.UPGRADE_REQUEST_TWELVE')
                    retMsg = 'Comprehensive Pre-Restorative Alignment Simulation Requested';
                }

                if (log.smile_design_upgrade_confirm.plan === 'twelveStep') {
                    retMsg = '14 step Pre-Restorative Alignment Simulation Requested';
                }
            }

            // For Smile Design Case
            if(log.status.status === 'PLAN_APPROVED' && smileDesignProductListArray.includes(log.case_type)){
                const text = this.get('log.TREATMENNT_PLAN_APPROVED');
                const regex = /{{plan_no}}/g;
                const updatedString = text.replace(regex, (_, param) => {
                    return log.treatment_plan.plan_no;
                });
                retMsg = { msg: updatedString + (approvementTypeLabel ? `(${approvementTypeLabel})` : ''), external: true };
            }
            if (caseType === CASE_TYPE.SELF_DESIGN && log.status.status === 'PLAN_APPROVED') {
                const message = this.get('log.TREATMENNT_PLAN_APPROVED').replace(
                    '{{plan_no}}',
                    log.treatment_plan?.plan_no ?? ''
                );
                const details = formatSelfDesignDetails(log.treatment_plan?.self_design, this.get.bind(this));
                retMsg = typeof retMsg === 'object'
                    ? { ...retMsg, msg: details ? `${message} - ${details}` : message }
                    : details
                    ? `${message} - ${details}`
                    : message;
            }
            if (caseType === CASE_TYPE.SELF_DESIGN && log.status.status === CASE_STATUS.DELIVERED) {
                const batchNo = Number.isInteger(log.batch_no) && log.batch_no > 0 ? String(log.batch_no) : null;
                const template = this.get('log.SELF_DESIGN_ALIGNER_BATCH_DELIVERED');
                const batchMessage = batchNo
                    ? template.replace('{{batch_no}}', batchNo)
                    : template.replace(' {{batch_no}}', '');
                const details = formatSelfDesignDetails(log.treatment_plan?.self_design, this.get.bind(this));
                retMsg = details ? `${batchMessage} - ${details}` : batchMessage;
            }
            if (
                smileDesignProductListArray.includes(log.case_type) &&
                log.status.status === 'PLAN_UPLOADED' &&
                log.treatment_plan?.smileDesign_exocad_pre === undefined &&
                log.treatment_plan?.smileDesign_exocad_post === undefined
            ) {
                delete retMsg.PLAN_UPLOADED;
            }
            break;
        case 'UPLOAD':
            if (log.case_retainer_request && log.case_retainer_request.retainer_id) {
                let msg = `${this.get('log.RETAINER_REQUESTED')} ${log.case_retainer_request.retainer_no}`;
                if (log.case_retainer_request.retainer_request_id && log.case_retainer_request.retainer_type_label) {
                    // has retainer_request_id means retainer request is new and integrated with retainer_request table
                    const { upper_retainer_type_label, lower_retainer_type_label } =
                        log.case_retainer_request.retainer_type_label;
                    const drRequestLabel = this.get('log.DIFF_DR_RETAINER_REQUESTED');
                    const upperTypeLabel = upper_retainer_type_label
                        ? `${this.get('@DiffRetainer.formOptions.upper')} - ${upper_retainer_type_label}`
                        : null;
                    const lowerTypeLabel = lower_retainer_type_label
                        ? `${this.get('@DiffRetainer.formOptions.lower')} - ${lower_retainer_type_label}`
                        : null;
                    const retainerNoLabel = log.case_retainer_request.retainer_no
                        ? this.get('log.DIFF_DR_RETAINER_REQUESTED_REF').replace(
                              '{{retainer_no}}',
                              log.case_retainer_request.retainer_no
                          )
                        : null;

                    if (upperTypeLabel && lowerTypeLabel) {
                        msg = `${drRequestLabel} ${upperTypeLabel}, ${lowerTypeLabel} (${retainerNoLabel})`;
                    } else if (upperTypeLabel) {
                        msg = `${drRequestLabel} ${upperTypeLabel} (${retainerNoLabel})`;
                    } else if (lowerTypeLabel) {
                        msg = `${drRequestLabel} ${lowerTypeLabel} (${retainerNoLabel})`;
                    }
                }
                retMsg = { msg: msg, external: true };
                break;
            } else if (log.case_refinement && log.case_refinement.refinement_id) {
                let user;
                if (['SADMIN', 'ADMIN'].includes(log.role)) {
                    user = this.get('log.REFINEMENT_X_SUBMITTED.admin');
                } else {
                    user = this.get('log.REFINEMENT_X_SUBMITTED.doctor');
                }
                // retMsg = `${this.get('log.REFINEMENT_X_SUBMITTED.REFINEMENT')} ${log.case_refinement.refinement_no} ${this.get('log.REFINEMENT_X_SUBMITTED.SUBMITTED')}`
                retMsg = `${this.get('log.REFINEMENT_X_SUBMITTED.REFINEMENT')} ${log.case_refinement.refinement_no} ${this.get('log.REFINEMENT_X_SUBMITTED.SUBMITTED')} ${user}`;
                break;
            } else if (log.defect_resubmit && log.defect_resubmit.refinement_id) {
                retMsg = `${this.get('log.DEFECT_RESUBMIT')} ${log.defect_resubmit.refinement_no}`;
                break;
            } else if (log.case_finishing_retainer?.finishing_retainer_id) {
                retMsg = {
                    msg: `${this.get('log.DOCTOR_REQUEST')}: ${this.get('log.finishing_retainer')}`,
                    external: true,
                };
                break;
            } else if (log.aligner_request && log.aligner_request.request_id) {
                let substr = '';
                if (log.aligner_request.type !== 'REQUEST') {
                    substr = `[Type: ${log.aligner_request.type[0] + log.aligner_request.type.toLowerCase().slice(1)}]`;
                }
                retMsg = {
                    msg: `${this.get('log.ALIGNERS_REQUESTED')} ${substr} (${this.get('log.STEP')}: ${log.aligner_request.detail.map((v) => bindGetAlignerLogMsg(v)).join(', ')})`,
                    external: true,
                };
                break;
            } else if (log.case_file && log.case_file.case_file_id) {
                if (
                    String.isNotBlank(log.case_file.radio0) ||
                    String.isNotBlank(log.case_file.radio1) ||
                    String.isNotBlank(log.case_file.radio2)
                ) {
                    retMsg = { msg: this.get('log.UPLOADED_RADIOGRAPHS'), external: false };
                    break;
                } else {
                    retMsg = { msg: this.get('log.UPLOADED_PHOTOGRAPHS'), external: false };
                    break;
                }
            } else if (log.case_impression && log.case_impression.case_impression_id) {
                retMsg = this.get('log.UPLOADED_IMPRESSION');
                break;
            } else if (log.case_detail && log.case_detail.case_detail_id) {
                retMsg = this.get(':fields.questionnaires');
                break;
            } else if (log.treatment_plan && log.treatment_plan.plan_id) {
                retMsg = this.get('log.UPLOADED_TREATMENT_PLAN') + ' ' + log.treatment_plan.plan_no;
                break;
            } else if (log.stl_pair && log.stl_pair.pair_id) {
                retMsg = this.get('log.UPLOADED_INDIVIDUAL_STL_FILES');
                break;
            }
            break;
        case 'UPDATE':
            if (
                log.case_finishing_retainer?.finishing_retainer_id &&
                log.case_finishing_retainer?.delivered_flag &&
                log.case_finishing_retainer?.status_param2 === null
            ) {
                retMsg = this.get('log.FINISHING_RETAINER') + ': ' + this.get('log.DELIVERED');
                break;
            }
            if (log.case_finishing_retainer?.status_param2 === 'RETAINER_PRODUCTION') {
                retMsg = this.get('log.FINISHING_RETAINER') + ': ' + this.get('log.PRODUCTION');
                break;
            }
            if (log.aligner_request && log.aligner_request.request_id && log.aligner_request.delivered_flag) {
                retMsg = this.get('log.ALIGNERS_STEP_DELIVERED').replace(
                    '{{step}}',
                    `(${this.get('log.STEP')}: ${log.aligner_request.detail.map((v) => bindGetAlignerLogMsg(v)).join(', ')})`
                );
                break;
            } else if (log.case_retainer_request && log.case_retainer_request.retainer_id) {
                // for retainer request after order different retainer feature
                if (log.case_retainer_request.retainer_request_id) {
                    const isProductionInProgressStatus =
                        log.case_retainer_request.status &&
                        log.case_retainer_request.status === RETAINER_REQUEST_STATUS.RETAINER_REQUEST_PRO;
                    const isDeliverdStatus =
                        log.case_retainer_request.status &&
                        log.case_retainer_request.status === RETAINER_REQUEST_STATUS.RETAINER_REQUEST_DEL;
                    const getRetainerLabel = (retainerTypeLabel, retainerNo) => {
                        const { upper_retainer_type_label, lower_retainer_type_label } = retainerTypeLabel;
                        const upperTypeLabel = upper_retainer_type_label
                            ? `${this.get('@DiffRetainer.formOptions.upper')} - ${upper_retainer_type_label}`
                            : null;
                        const lowerTypeLabel = lower_retainer_type_label
                            ? `${this.get('@DiffRetainer.formOptions.lower')} - ${lower_retainer_type_label}`
                            : null;
                        const retainerNoLabel = retainerNo
                            ? this.get('log.DIFF_DR_RETAINER_REQUESTED_REF').replace('{{retainer_no}}', retainerNo)
                            : null;

                        if (upperTypeLabel && lowerTypeLabel) {
                            return `${upperTypeLabel}, ${lowerTypeLabel} (${retainerNoLabel})`;
                        } else if (upperTypeLabel) {
                            return `${upperTypeLabel} (${retainerNoLabel})`;
                        } else if (lowerTypeLabel) {
                            return `${lowerTypeLabel} (${retainerNoLabel})`;
                        }
                        return '';
                    };
                    // both production_flag and delivered_flag are boolean and eventually will both be true during the workflow
                    // so need to add checking on status to determine the current step
                    if (log.case_retainer_request.production_flag && isProductionInProgressStatus) {
                        const retainerLabel = getRetainerLabel(
                            log.case_retainer_request.retainer_type_label,
                            log.case_retainer_request.retainer_no
                        );
                        retMsg = this.get('log.RETAINER_REQUEST_PRODUCTION').replace(
                            '{{retainer}}',
                            retainerLabel || ''
                        );
                    }
                    if (log.case_retainer_request.delivered_flag && isDeliverdStatus) {
                        const retainerLabel = getRetainerLabel(
                            log.case_retainer_request.retainer_type_label,
                            log.case_retainer_request.retainer_no
                        );
                        retMsg = this.get('log.RETAINER_REQUEST_DELIVERED').replace(
                            '{{retainer}}',
                            retainerLabel || ''
                        );
                    }
                } else if (
                    log.case_retainer_request &&
                    log.case_retainer_request.retainer_id &&
                    log.case_retainer_request.delivered_flag &&
                    !log.case_retainer_request.retainer_request_id
                ) {
                    // for retainer request before order different retainer feature
                    retMsg = this.get('log.RETAINER_DELIVERED').replace(
                        '{{retainer_no}}',
                        log.case_retainer_request.retainer_no
                    );
                }
                
            } else if (log.treatment_plan && log.treatment_plan.plan_id) {
                const logKey =
                    caseType === CASE_TYPE.SELF_DESIGN ? 'log.TREATMENNT_PLAN_UPLOADED' : 'log.TREATMENNT_PLAN_UPDATED';
                retMsg = this.get(logKey).replace('{{plan_no}}', log.treatment_plan.plan_no); // + ' ' + log.status.status_remarks
                break;
            } else if (log.status?.status === 'RECEIVED') {
                retMsg = this.get('log.RECEIVED_STATUS_UPDATE')
                    .replace('{{status_param1}}', log.status.status_param1)
                    .replace('{{status_param2}}', log.status.status_param2);
                break;
            }
            if (log.status?.status_param2 === 'MOCKUP_ORDER') {
                const productLabel = log.case_mockup_order?.product_label;
                const displayProductLabel = mockupOrderOptionLabels[productLabel] || productLabel;
                retMsg = StatusMsgDict[log.status.status_param2]
                    .replace('{{product_label}}', displayProductLabel)
                    .replace('{{status_param1}}', log.status.status_param1);
            }
            break;
        case 'UNDO':
            if (log.treatment_plan && log.treatment_plan.plan_id) {
                retMsg = this.get('log.TREATMENNT_PLAN_UPDATED').replace('{{plan_no}}', log.treatment_plan.plan_no); // + ' ' + log.status.status_remarks
                break;
            }
        default:
            return this.get('log.UNKNOWN');
    }
    const refinement_no =
        log.refinement_no ??
        log.case_detail?.refinement_no ??
        log.case_file?.refinement_no ??
        log.case_impression?.refinement_no ??
        log.case_refinement?.refinement_no;

    if (refinement_no && refinement_no > 0) {
        if (typeof retMsg === 'object') {
            // object when it is a refinement or resubmit which use to show doctor action different color
            if (log.defect_resubmit) {
                refinement_mode = 'defect resubmit';
            }
            if (log.case_refinement) {
                refinement_mode = 'refinement';
            }
            retMsg.msg =
                refinement_mode === 'defect resubmit'
                    ? `( D${refinement_no} )  ${retMsg.msg}`
                    : `( R${refinement_no} ) ${retMsg.msg}`;
        } else if (typeof retMsg === 'string') {
            if (log.defect_resubmit) {
                refinement_mode = 'defect resubmit';
            }
            if (log.case_refinement) {
                refinement_mode = 'refinement';
            }

            retMsg =
                refinement_mode == 'defect resubmit'
                    ? `( D${refinement_no} )  ${retMsg}`
                    : `( R${refinement_no} ) ${retMsg}`;
        }
    }
    return retMsg;
}

export default {
    translateLogs,
};
